"use client";

import { useState } from "react";
import { DEFAULT_UI } from "@/lib/ui-defaults";
import { useUI } from "@/lib/ui-context";

export interface FreeVhfKeyPhrasesCollapsibleProps {
  /** List of key phrases to show when expanded (locale-aware, from scenario). */
  phrases: string[];
  /** Optional id for the block (a11y). */
  id?: string;
}

/**
 * M1/M2 key phrases: collapsible "Show key phrases" (closed by default); expanded shows list.
 */
export default function FreeVhfKeyPhrasesCollapsible({
  phrases,
  id = "free-vhf-key-phrases",
}: FreeVhfKeyPhrasesCollapsibleProps) {
  const ui = useUI();
  const fb = DEFAULT_UI.freeVhf?.keyPhrases;
  const regionLabel = ui.freeVhf?.keyPhrases?.label ?? fb?.label ?? "";
  const showLabel = ui.freeVhf?.keyPhrases?.show ?? fb?.show ?? "";
  const hideLabel = ui.freeVhf?.keyPhrases?.hide ?? fb?.hide ?? "";

  const [expanded, setExpanded] = useState(false);

  if (!phrases?.length) return null;

  return (
    <div id={id} className="space-y-2" role="region" aria-label={regionLabel}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-lg border border-[var(--neutral-border)] text-content text-sm font-medium hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-interactive-focus focus-visible:outline-offset-2"
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
      >
        <span
          className="inline-flex shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary flex items-center justify-center"
          aria-hidden
        >
          <svg
            className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        {expanded ? hideLabel : showLabel}
      </button>
      {expanded && (
        <ul
          id={`${id}-content`}
          className="list-disc list-inside rounded-lg border border-[var(--neutral-border)] bg-neutral-50 px-4 py-3 text-sm text-muted space-y-1"
        >
          {phrases.map((phrase, i) => (
            <li key={i}>{phrase}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
